import {NavLinkProps} from 'quickstart/components/layout/NavLink'
import {truthy} from 'quickstart/types'
import {logger, tryJson} from 'tizra'
import * as A from '../admin'

const log = logger('quickstart/blocks/NavBlock/admin')

export const NavAdmin = () => (
  <p>
    Nav is configured globally in <A.NavOut tab="Design" subtab="nav" />.
  </p>
)

export interface NavBlockGlobalConfig {
  links: Array<{text: string; link: string}>
  profileMenu: {
    includeAccount: boolean
    includeBookshelf: boolean
  }
  includeSignIn: boolean
  includeCart: boolean
  quickSearchPlaceholder: string
  nestedNavConfigJson: string
  nestedNavConfig: {
    items: NavLinkProps[]
    profileItems: NavLinkProps[]
  }
}

export const defaultGlobalConfig: NavBlockGlobalConfig = {
  links: [
    {text: 'Home', link: '/'},
    {text: 'Resources', link: '/resources'},
    {text: 'Collections', link: '/collection/'},
  ],
  profileMenu: {
    includeAccount: false,
    includeBookshelf: false,
  },
  includeSignIn: true,
  includeCart: false,
  quickSearchPlaceholder: '',
  nestedNavConfigJson: '',
  nestedNavConfig: {
    items: [{label: 'Home', link: '/'}],
    profileItems: [],
  },
}

const UI_MANAGES_NESTED_NAV = false

export const globalMigrate: A.GlobalMigrate<NavBlockGlobalConfig> = ({
  defaultGlobalMigrate,
  globalConfig: unknownGlobalConfig,
  hacks,
  ...props
}) => {
  let globalConfig = {
    ...(unknownGlobalConfig as any),
  } as Partial<NavBlockGlobalConfig>

  // Migrate from (really old) static four links to dynamic.
  if (!globalConfig.links && 'link0' in globalConfig) {
    const {text0, link0, text1, link1, text2, link2, text3, link3, ...rest} =
      globalConfig as any
    globalConfig = {
      ...rest,
      links: [
        {text: text0, link: link0},
        {text: text1, link: link1},
        {text: text2, link: link2},
        {text: text3, link: link3},
      ],
    }
  }

  // Migrate to nested nav.
  //
  // This is complex, because there are three potential sources of config:
  // - saved nestedNavConfigJson
  // - saved nestedNavConfig
  // - old config
  //
  // Which is primary depends on whether the hack is enabled and whether we have
  // UI to managed the new nestedNavConfig.
  //
  const nestedNavHack = hacks.includes('nestedNav')
  const {links, profileMenu} = {...defaultGlobalConfig, ...globalConfig}
  const migratedNestedNavConfig = {
    items: links
      .map(({text, link}) => text && link && {label: text, href: link})
      .filter(truthy),
    profileItems: [
      profileMenu.includeAccount && ({type: 'account'} as const),
      profileMenu.includeBookshelf && ({type: 'bookshelf'} as const),
    ].filter(truthy),
  }
  if (nestedNavHack) {
    // When the hack is enabled, then JSON rules so long as it exists, otherwise
    // we migrate into it. Ignore anything saved directly to nestedNavConfig.
    globalConfig.nestedNavConfig =
      tryJson(globalConfig.nestedNavConfigJson, e => log.browser.error(e)) ||
      migratedNestedNavConfig
    globalConfig.nestedNavConfigJson =
      globalConfig.nestedNavConfigJson ||
      JSON.stringify(globalConfig.nestedNavConfig, null, 2)
  } else if (UI_MANAGES_NESTED_NAV) {
    // When the UI gloriously manages the nested nav, then migrate from JSON
    // until saved, then kill the JSON.
    globalConfig.nestedNavConfig =
      tryJson(globalConfig.nestedNavConfigJson, e => log.browser.error(e)) ||
      globalConfig.nestedNavConfig ||
      migratedNestedNavConfig
    globalConfig.nestedNavConfigJson = ''
  } else {
    // No hack, and UI doesn't manage yet, so we're migrating from old config.
    // Overwrite anything saved to nestedNavConfigJson thus far.
    globalConfig.nestedNavConfig = migratedNestedNavConfig
    globalConfig.nestedNavConfigJson = JSON.stringify(
      globalConfig.nestedNavConfig,
      null,
      2,
    )
  }

  return defaultGlobalMigrate({...props, globalConfig})
}

export const NavGlobalAdmin: A.Admin<NavBlockGlobalConfig> = () => {
  const nestedNavEnabled = A.useHack('nestedNav')
  return (
    <>
      <A.Group>
        <A.CheckRight label="Include sign in" name="includeSignIn" />
        <A.CheckRight label="Include shopping cart" name="includeCart" />
      </A.Group>
      <A.Group title="Nested Nav TECH PREVIEW" hidden={!nestedNavEnabled}>
        <A.Textarea
          label="Config JSON"
          name="nestedNavConfigJson"
          hidden={!nestedNavEnabled}
        />
      </A.Group>
      <A.Group title="Links" hidden={nestedNavEnabled}>
        <A.FieldArray name="links" orderable>
          {({fields}) =>
            fields.map((name, i) => (
              <A.Row alignItems="center">
                <A.RowItem>⣶</A.RowItem>
                <A.Input placeholder="Text" name={`${name}.text`} />
                <A.Input placeholder="URL" name={`${name}.link`} />
                <A.Button onClick={() => fields.remove(i)}>X</A.Button>
              </A.Row>
            ))
          }
          {({fields}) => (
            <A.Button mt="1rem" onClick={() => fields.push({})}>
              Add link
            </A.Button>
          )}
        </A.FieldArray>
      </A.Group>
      <A.Group title="Profile menu" hidden={nestedNavEnabled}>
        <A.CheckRight
          label="Include bookshelf"
          name="profileMenu.includeBookshelf"
        />
        <A.CheckRight
          label="Include account"
          name="profileMenu.includeAccount"
        />
      </A.Group>
      <A.Group title="Quick search">
        <A.Input
          label="Custom placeholder text"
          name="quickSearchPlaceholder"
        />
      </A.Group>
    </>
  )
}
